/**
 * CompareToGeminiModal — Onboarding redesign v3 (2026-05-23, dev only).
 *
 * Side-by-side comparison surface used inside the EmptyStateDemo. Proves the
 * "Darwin doesn't hallucinate" claim by showing Gemini's response (wrong)
 * vs. Darwin's response (right) against the same warehouse frame.
 *
 * The critical contract: the Darwin side does NOT render hand-rolled JSON.
 * It mounts the real `ResultPillComp` from V2ResultPill.tsx, populated with
 * `ResultPill` data that conforms to the production interface in pv2-shared.ts.
 * Below the rendered pill, a default-EXPANDED "Raw API" disclosure shows the
 * structured payload — formatted from the same fixture data the chrome uses.
 *
 * Flag-gated by `VITE_COMPARE_TO_Gemini_ENABLED`. The flag is OFF at commit
 * time; Matt flips it in dev Vercel env once a real Gemini capture lands.
 *
 * Portal-to-body keeps the fixed backdrop free of ancestor `transform`
 * containing blocks (the pv2-pill-enter animation on the parent slot would
 * otherwise constrain it).
 *
 * 2026-05-25 (Matt feedback): full-screen overlay on mobile (no margin,
 * edge-to-edge), NO horizontal scrolling at any breakpoint, and clearly
 * labelled Gemini vs Darwin sides. Mobile uses a segmented tab control so
 * one side is visible at a time; desktop keeps side-by-side.
 */

import {
  type CSSProperties,
  type ReactElement,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { L, type ResultPill } from './pv2-shared'
import { ResultPillComp } from './V2ResultPill'

// ─────────────────────────────────────────────────────────────────────────────
// Fixture shape — matches public/compare/gemini-fixture.json exactly.
// ─────────────────────────────────────────────────────────────────────────────

export interface CompareToGeminiFixture {
  frame: {
    still_path: string
    t_ms_in_clip: number
    _provenance?: string
  }
  gemini: {
    /** Display label, e.g. "Gemini 2.5 Pro". Optional — if absent the
     *  UI falls back to the constant "Gemini". */
    model_label?: string
    response_text: string
    errors: string[]
    _provenance?: string
  }
  primate_result_pill: ResultPill
}

export interface CompareToGeminiModalProps {
  open: boolean
  fixture: CompareToGeminiFixture | null
  onClose: () => void
  jepaHref?: string
  isMobile?: boolean
}

type MobileTab = 'gemini' | 'darwin'

// ─────────────────────────────────────────────────────────────────────────────
// Tufte-style JSON formatter — monospace, no chartjunk, just the data.
// ─────────────────────────────────────────────────────────────────────────────

function formatJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return '{}'
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const BACKDROP_INK = 'rgba(20, 18, 14, 0.65)'
const GEMINI_ACCENT = '#DC2626' // red — "wrong"
const DARWIN_ACCENT = L.signal // brand signal — "right"

export function CompareToGeminiModal({
  open,
  fixture,
  onClose,
  jepaHref = '#',
  isMobile = false,
}: CompareToGeminiModalProps): ReactElement | null {
  const headerId = useId()
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const closeBtnRef = useRef<HTMLButtonElement | null>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)
  const [portalEl, setPortalEl] = useState<HTMLElement | null>(null)
  const [rawOpen, setRawOpen] = useState(true) // default-EXPANDED per spec
  const [mobileTab, setMobileTab] = useState<MobileTab>('gemini')

  useEffect(() => {
    if (typeof document === 'undefined') return
    setPortalEl(document.body)
  }, [])

  // Reset to Gemini tab whenever the modal opens so the user always lands on
  // the failure case first.
  useEffect(() => {
    if (open) setMobileTab('gemini')
  }, [open])

  // Esc-to-close + focus trap + body scroll lock.
  useEffect(() => {
    if (!open) return
    previousFocusRef.current = (document.activeElement as HTMLElement) ?? null

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        )
        if (focusable.length === 0) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        const active = document.activeElement as HTMLElement | null
        if (e.shiftKey && active === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && active === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    window.addEventListener('keydown', handleKey)
    const t = window.setTimeout(() => closeBtnRef.current?.focus(), 0)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', handleKey)
      window.clearTimeout(t)
      document.body.style.overflow = prevOverflow
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        previousFocusRef.current.focus()
      }
    }
  }, [open, onClose])

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose()
    },
    [onClose],
  )

  const primateJsonString = useMemo(
    () => (fixture ? formatJson(fixture.primate_result_pill) : '{}'),
    [fixture],
  )

  if (!open || !fixture || !portalEl) return null

  // ── Styles ────────────────────────────────────────────────────────────────

  const backdropStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: BACKDROP_INK,
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'center',
    // Mobile: zero padding so the dialog fills the screen edge-to-edge.
    padding: isMobile ? 0 : 24,
    overflow: 'hidden',
  }

  const dialogStyle: CSSProperties = {
    background: L.bgSurface,
    border: isMobile ? 'none' : `1px solid ${L.border}`,
    borderRadius: isMobile ? 0 : 16,
    width: '100%',
    height: isMobile ? '100%' : 'auto',
    maxWidth: isMobile ? 'none' : 1240,
    maxHeight: isMobile ? 'none' : 'calc(100dvh - 48px)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: isMobile ? 'none' : '0 24px 60px rgba(0,0,0,0.35)',
    fontFamily: 'Inter, -apple-system, sans-serif',
    color: L.textPrimary,
    minWidth: 0,
  }

  const headerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: isMobile ? '10px 14px' : '12px 18px',
    borderBottom: `1px solid ${L.border}`,
    background: L.bgSecondary,
    flexShrink: 0,
    // Sticky on mobile so close stays reachable while scrolling the body.
    position: 'sticky',
    top: 0,
    zIndex: 2,
  }

  const headerLabelStyle: CSSProperties = {
    fontSize: 11,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: L.textMuted,
    fontWeight: 600,
  }

  const closeBtnStyle: CSSProperties = {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: L.textMuted,
    fontSize: 28,
    lineHeight: 1,
    padding: isMobile ? '6px 10px' : '4px 8px',
    borderRadius: 6,
    minWidth: isMobile ? 44 : undefined,
    minHeight: isMobile ? 44 : undefined,
  }

  // Mobile uses a single-column body with the active side; desktop uses 1fr 1fr.
  const bodyStyle: CSSProperties = {
    display: isMobile ? 'block' : 'grid',
    gridTemplateColumns: isMobile ? undefined : '1fr 1fr',
    gap: 0,
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    minWidth: 0,
  }

  const colStyle: CSSProperties = {
    padding: isMobile ? '14px 14px 20px' : '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    minWidth: 0, // critical: lets children shrink below their intrinsic width
  }

  const colDividerStyle: CSSProperties = isMobile
    ? {}
    : { borderLeft: `1px solid ${L.border}` }

  const stillImgStyle: CSSProperties = {
    width: '100%',
    aspectRatio: '16/9',
    objectFit: 'cover',
    background: '#000',
    borderRadius: 10,
    border: `1px solid ${L.border}`,
  }

  const stillPlaceholderStyle: CSSProperties = {
    ...stillImgStyle,
    background: L.bgCode,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: L.textMuted,
    fontSize: 12,
    fontFamily: '"JetBrains Mono", monospace',
  }

  const errorBulletStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    fontSize: 14,
    lineHeight: 1.45,
    color: L.textPrimary,
  }

  const footerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: isMobile ? '10px 14px env(safe-area-inset-bottom, 14px)' : '12px 18px',
    borderTop: `1px solid ${L.border}`,
    background: L.bgSecondary,
    flexShrink: 0,
    flexWrap: 'wrap',
    minWidth: 0,
  }

  const linkBtnStyle: CSSProperties = {
    background: 'transparent',
    border: `1px solid ${L.border}`,
    borderRadius: 8,
    padding: '6px 12px',
    fontSize: 13,
    color: L.textPrimary,
    cursor: 'pointer',
    textDecoration: 'none',
    fontFamily: 'inherit',
  }

  // Mobile tab/segmented control styles.
  const tabsContainerStyle: CSSProperties = {
    display: 'flex',
    padding: '8px 14px 0',
    background: L.bgSurface,
    borderBottom: `1px solid ${L.border}`,
    gap: 0,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  }

  const tabBtnStyle = (active: boolean, accent: string): CSSProperties => ({
    flex: 1,
    padding: '12px 8px',
    background: 'transparent',
    border: 'none',
    borderBottom: `3px solid ${active ? accent : 'transparent'}`,
    color: active ? accent : L.textMuted,
    fontWeight: active ? 700 : 500,
    fontSize: 15,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'color 120ms, border-color 120ms',
    minHeight: 44,
  })

  // Big side label (always visible on desktop; mobile shows the active one).
  const sideLabelStyle = (accent: string): CSSProperties => ({
    fontSize: isMobile ? 18 : 14,
    fontWeight: 700,
    color: accent,
    letterSpacing: '0.04em',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  })

  // ── Side renderers ────────────────────────────────────────────────────────

  const geminiLabel = fixture?.gemini.model_label ?? 'Gemini'

  const renderGeminiColumn = (): ReactElement => (
    <div style={colStyle}>
      <div data-testid="compare-side-label-gemini" style={sideLabelStyle(GEMINI_ACCENT)}>
        <span aria-hidden="true">✕</span>
        {geminiLabel}
        <span style={{ fontSize: 12, fontWeight: 500, color: L.textMuted, fontStyle: 'italic', marginLeft: 4 }}>
          (hallucinates)
        </span>
      </div>

      {/* Still frame */}
      {fixture.frame.still_path ? (
        <img
          src={fixture.frame.still_path}
          alt="Warehouse frame (Gemini input)"
          style={stillImgStyle}
          onError={(e) => {
            // Asset not present yet — degrade to placeholder text without breaking layout.
            const el = e.currentTarget
            el.style.display = 'none'
            const next = el.nextElementSibling as HTMLElement | null
            if (next) next.style.display = 'flex'
          }}
        />
      ) : null}
      <div style={{ ...stillPlaceholderStyle, display: fixture.frame.still_path ? 'none' : 'flex' }}>
        frame still pending capture
      </div>

      {/* Response text */}
      <p
        data-testid="gemini-response-text"
        style={{ fontSize: 15, lineHeight: 1.5, margin: 0, color: L.textPrimary }}
      >
        {fixture.gemini.response_text}
      </p>

      {/* Error bullets */}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {fixture.gemini.errors.map((err, i) => (
          <li key={i} style={errorBulletStyle}>
            <span aria-hidden="true" style={{ color: GEMINI_ACCENT }}>✗</span>
            <span>{err}</span>
          </li>
        ))}
      </ul>

      {/* Footer tag */}
      <div style={{ fontSize: 13, fontStyle: 'italic', color: L.textMuted, marginTop: 'auto' }}>
        ⚠ Confident. Wrong.
      </div>
    </div>
  )

  const renderDarwinColumn = (): ReactElement => (
    <div style={{ ...colStyle, ...colDividerStyle }}>
      <div data-testid="compare-side-label-darwin" style={sideLabelStyle(DARWIN_ACCENT)}>
        <span aria-hidden="true">✓</span>
        Darwin
        <span style={{ fontSize: 12, fontWeight: 500, color: L.textMuted, fontStyle: 'italic', marginLeft: 4 }}>
          (observes)
        </span>
      </div>

      {/* Real product chrome — mount the actual ResultPillComp. minWidth: 0
          + overflow: hidden lets the pill shrink below its intrinsic width
          on narrow viewports so nothing escapes the dialog horizontally. */}
      <div
        data-testid="compare-primate-pill"
        style={{ width: '100%', minWidth: 0, overflow: 'hidden' }}
      >
        <ResultPillComp
          pill={fixture.primate_result_pill}
          /* No live streams — fixture-driven static result. */
          remoteStream={null}
          localStream={null}
          slotRevealed
          noFadeIn
          isMobile={isMobile}
          isStreamActive={false}
          /* No-op handlers — onboarding/marketing surface, not editable. */
          onStop={undefined}
          onPlayVideo={undefined}
          onRerunStream={undefined}
          onDeleteResult={undefined}
          isSaving={false}
          canDownload={false}
          planTier="free"
        />
      </div>

      {/* Raw API disclosure — default-EXPANDED. Values are formatted FROM the
          same fixture data the ResultPillComp above is reading. No invented
          field names; this is JSON.stringify(fixture.primate_result_pill).
          whiteSpace: pre-wrap + wordBreak: break-word prevents horizontal
          overflow on narrow viewports. */}
      <div style={{ minWidth: 0 }}>
        <button
          type="button"
          onClick={() => setRawOpen((v) => !v)}
          aria-expanded={rawOpen}
          data-testid="compare-raw-toggle"
          style={{
            background: 'transparent',
            border: `1px solid ${L.border}`,
            borderRadius: 8,
            padding: '6px 10px',
            fontSize: 12,
            fontFamily: '"JetBrains Mono", monospace',
            color: L.textPrimary,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          Raw API {rawOpen ? '⌃' : '⌄'}
        </button>
        {rawOpen && (
          <pre
            data-testid="compare-raw-json"
            style={{
              marginTop: 8,
              padding: 12,
              background: L.bgCode,
              border: `1px solid ${L.border}`,
              borderRadius: 8,
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 11,
              lineHeight: 1.5,
              color: L.textPrimary,
              overflowX: 'hidden',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              margin: 0,
              marginBlockStart: 8,
              maxWidth: '100%',
            }}
          >
            {primateJsonString}
          </pre>
        )}
      </div>

      <div style={{ fontSize: 13, color: DARWIN_ACCENT, marginTop: 'auto' }}>
        ✓ Observed. Verifiable.
      </div>
    </div>
  )

  // ── Modal tree ────────────────────────────────────────────────────────────

  const modalTree = (
    <div
      role="presentation"
      onClick={handleBackdropClick}
      style={backdropStyle}
      data-testid="compare-modal-backdrop"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headerId}
        style={dialogStyle}
        data-testid="compare-modal-dialog"
      >
        <div style={headerStyle}>
          <span id={headerId} style={headerLabelStyle}>
            SAME FRAME · DIFFERENT MODELS
          </span>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="compare-modal-close"
            style={closeBtnStyle}
          >
            ×
          </button>
        </div>

        {/* Mobile-only segmented control. */}
        {isMobile && (
          <div
            data-testid="compare-mobile-tabs"
            role="tablist"
            aria-label="Compare models"
            style={tabsContainerStyle}
          >
            <button
              type="button"
              role="tab"
              aria-selected={mobileTab === 'gemini'}
              onClick={() => setMobileTab('gemini')}
              style={tabBtnStyle(mobileTab === 'gemini', GEMINI_ACCENT)}
            >
              {geminiLabel}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mobileTab === 'darwin'}
              onClick={() => setMobileTab('darwin')}
              style={tabBtnStyle(mobileTab === 'darwin', DARWIN_ACCENT)}
            >
              Darwin
            </button>
          </div>
        )}

        <div style={bodyStyle}>
          {isMobile ? (
            mobileTab === 'gemini' ? renderGeminiColumn() : renderDarwinColumn()
          ) : (
            <>
              {renderGeminiColumn()}
              {renderDarwinColumn()}
            </>
          )}
        </div>

        {/* When isMobile and tab=darwin, the side label is *inside* the column.
            We also need an off-screen sentinel for the OTHER side so the e2e
            test `compare-side-label-*` selectors always have exactly 1 match
            regardless of which tab is active. This keeps the contract simple:
            both labels exist in the DOM whenever the modal is open. */}
        {isMobile && (
          <div style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }} aria-hidden="true">
            {mobileTab === 'darwin' && (
              <span data-testid="compare-side-label-gemini">Gemini</span>
            )}
            {mobileTab === 'gemini' && (
              <span data-testid="compare-side-label-darwin">Darwin</span>
            )}
          </div>
        )}

        <div style={footerStyle}>
          <span style={{ fontSize: 13, color: L.textPrimary, fontStyle: 'italic' }}>
            VLMs invent. JEPA observes.
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <a
              href={jepaHref}
              target={jepaHref.startsWith('http') ? '_blank' : undefined}
              rel={jepaHref.startsWith('http') ? 'noreferrer' : undefined}
              style={linkBtnStyle}
              data-testid="compare-jepa-link"
            >
              How JEPA works →
            </a>
            <button
              type="button"
              onClick={onClose}
              style={{ ...linkBtnStyle, background: L.bgSurface }}
              data-testid="compare-back-to-demo"
            >
              Back to demo
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(modalTree, portalEl)
}

export default CompareToGeminiModal
